import { useAppSelector } from 'app/hooks';
import { getFromBrandConfig } from 'brand-config';
import { selectOfferGroupCode } from 'services/offer/offerSelectors';
import { LEGAL_PATHS } from 'utils/constants';

type OverridableLegalKey = 'privacyPolicy' | 'termsOfService';

const CANONICAL_PATHS: Record<OverridableLegalKey, string> = {
  privacyPolicy: LEGAL_PATHS.privacyPolicy,
  termsOfService: LEGAL_PATHS.termsOfService,
};

const PARTNER_PATHS: Record<OverridableLegalKey, string> = {
  privacyPolicy: LEGAL_PATHS.partnerPrivacyPolicy,
  termsOfService: LEGAL_PATHS.partnerTermsOfService,
};

/**
 * Where a legal link should point for the current member.
 *
 * Legal links open in a new tab, and `rel='noreferrer'` implies `noopener`, so that tab gets
 * an empty sessionStorage and rehydrates no offer state. The group has to be resolved here,
 * in the tab that still has it, and named in the URL — the same split Angular makes between
 * `footer.component.ts:176` and its group-agnostic `PolicyComponent`.
 *
 * `selectOfferGroupCode` is written only by `getOfferDetails()` in the authenticated
 * bootstrap, so signed-out and enrollment screens keep the canonical paths, as in Angular,
 * where those run a separate footer with no group branch.
 */
function useLegalPath(key: OverridableLegalKey): string {
  const groupCode = useAppSelector(selectOfferGroupCode);
  const override = getFromBrandConfig('offerGroupLegalOverride');
  const isPartnerGroup = Boolean(
    groupCode && override?.groupCodes.includes(groupCode),
  );

  return isPartnerGroup ? PARTNER_PATHS[key] : CANONICAL_PATHS[key];
}

export default useLegalPath;
